import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowLeft, Mail } from 'lucide-react'

import { Button } from '@/components/ui'
import { appRoutes } from '@/constants'
import { getApiErrorMessage } from '@/lib/api-client'
import { useForgotPassword } from '../hooks'
import { forgotPasswordSchema, type ForgotPasswordValues } from '../schemas'
import { AuthLayout } from './auth-layout'
import { AuthField } from './auth-field'
import { zodResolver } from './zod-resolver'

export function ForgotPasswordPage() {
  const [submitting, setSubmitting] = useState(false)
  const mutation = useForgotPassword()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true)
    try {
      await mutation.mutateAsync(values)
      toast.success('Reset link sent', {
        description: 'If the email exists, a password reset link has been sent.',
      })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to send reset link'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter your account email and we'll send you a reset link."
      footer={
        <Link
          to={appRoutes.auth.login}
          className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
          label="Email Address"
          icon={<Mail />}
          placeholder="you@example.com"
          type="email"
          autoComplete="email"
          error={errors.email}
          {...register('email')}
        />

        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>
    </AuthLayout>
  )
}